import React, { useState, useEffect } from "react";
import Button from "../../components/common/Button";
import Input from "../../components/form/input/InputField";
import api from "../../utills/api";
import { GET_ALL_MODELS, ADD_MODELS, UPDATE_MODELS, GET_ALL_CATEGORIES, GET_ALL_SUBCATEGORIES } from "../../utills/endpoint";

interface Category {
  _id: string;
  name: string;
}

interface Subcategory {
  _id: string;
  name: string;
  categoryId?: string;
}

interface Model {
  _id: string;
  name: string;
  shape: string;
  model_url?: string;
  image2d_url?: string;
  height: number;
  depth: number;
  width: number;
  textureavailable: string[];
  colorsavailable: string[];
  category: string;
  subcategory: string;
  customizable: boolean;
  price: number;
  customizableProduct?: boolean;
  modelCustomisable?: string[]; // Array of model IDs for customizable models
}

interface CustomizableModel {
  _id?: string;
  name: string;
  shape: string;
  model_url?: string;
  image2d_url?: string;
  height: number;
  depth: number;
  width: number;
  textureavailable: string[];
  colorsavailable: string[];
  price: number;
  customizableProduct: boolean;
  customizable: boolean;
}

const AllModels: React.FC = () => {
  const [models, setModels] = useState<Model[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>("");
  
  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [editingModel, setEditingModel] = useState<Model | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  
  // Form states
  const [modelForm, setModelForm] = useState({
    name: "",
    shape: "",
    model_url: "",
    image2d_url: "",
    height: "",
    depth: "",
    width: "",
    textureavailable: [] as string[],
    colorsavailable: [] as string[],
    customizable: false,
    price: ""
  });

  // File upload states
  const [modelFile, setModelFile] = useState<File | null>(null);
  const [image2dFile, setImage2dFile] = useState<File | null>(null);

  // Customizable models states
  const [customizableModels, setCustomizableModels] = useState<CustomizableModel[]>([]);
  const [isCustomizableModalOpen, setIsCustomizableModalOpen] = useState<boolean>(false);
  const [customizableForm, setCustomizableForm] = useState({
    name: "",
    shape: "",
    model_url: "",
    image2d_url: "",
    height: "",
    depth: "",
    width: "",
    textureavailable: [] as string[],
    colorsavailable: [] as string[],
    price: ""
  });
  const [customizableModelFile, setCustomizableModelFile] = useState<File | null>(null);
  const [customizableImage2dFile, setCustomizableImage2dFile] = useState<File | null>(null);

  // Fetch data on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Fetch subcategories when category changes
  useEffect(() => {
    if (selectedCategory) {
      fetchSubcategories(selectedCategory);
    }
  }, [selectedCategory]);

  // Fetch models when both category and subcategory are selected
  useEffect(() => {
    if (selectedCategory && selectedSubcategory) {
      fetchModels();
    }
  }, [selectedCategory, selectedSubcategory]);

  const fetchModels = async () => {
    try {
      setLoading(true);
      const response = await api.get(`${GET_ALL_MODELS}?category=${selectedCategory}&subcategory=${selectedSubcategory}`);
      console.log("Models response:", response.data);
      
      if (response.data && response.data.data && response.data.data.models) {
        // Filter models where customizableProduct is false or undefined
        const filteredModels = response.data.data.models.filter((model: Model) => 
          model.customizableProduct === false || model.customizableProduct === undefined
        );
        setModels(filteredModels);
      }
    } catch (error) {
      console.error("Error fetching models:", error);
      setError("Failed to fetch models");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await api.get(GET_ALL_CATEGORIES);
      console.log(response.data);
      if (response.data && response.data.categories) {
        setCategories(response.data.categories);
        // Set first category as default selected
        if (response.data.categories.length > 0) {
          setSelectedCategory(response.data.categories[0]._id);
        }
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setError("Failed to fetch categories");
    } finally {
      setLoading(false);
    }
  };

  const fetchSubcategories = async (categoryId: string) => {
    try {
      setLoading(true);
      const response = await api.get(`${GET_ALL_SUBCATEGORIES}${categoryId}`);
      console.log("Subcategories response:", response.data);
      if (response.data && response.data.subcategories) {
        setSubcategories(response.data.subcategories);
        // Set first subcategory as default selected
        if (response.data.subcategories.length > 0) {
          setSelectedSubcategory(response.data.subcategories[0]._id);
        }
      }
    } catch (error) {
      console.error("Error fetching subcategories:", error);
      setError("Failed to fetch subcategories");
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value);
    setSelectedSubcategory(""); // Reset subcategory selection
  };

  const handleSubcategoryChange = (value: string) => {
    setSelectedSubcategory(value);
  };

  // Customizable model handlers
  const handleAddCustomizableModel = () => {
    setCustomizableForm({
      name: "",
      shape: "",
      model_url: "",
      image2d_url: "",
      height: "",
      depth: "",
      width: "",
      textureavailable: [],
      colorsavailable: [],
      price: ""
    });
    setCustomizableModelFile(null);
    setCustomizableImage2dFile(null);
    setIsCustomizableModalOpen(true);
  };

  const handleCloseCustomizableModal = () => {
    setIsCustomizableModalOpen(false);
    setCustomizableForm({
      name: "",
      shape: "",
      model_url: "",
      image2d_url: "",
      height: "",
      depth: "",
      width: "",
      textureavailable: [],
      colorsavailable: [],
      price: ""
    });
    setCustomizableModelFile(null);
    setCustomizableImage2dFile(null);
    setError("");
  };

  const handleRemoveCustomizableModel = async (index: number) => {
    const modelToRemove = customizableModels[index];
    
    // If the model has an _id, delete it from backend
    if (modelToRemove._id) {
      try {
        await api.delete(`${UPDATE_MODELS}/${modelToRemove._id}`);
      } catch (error) {
        console.error("Error deleting customizable model:", error);
        setError("Failed to delete customizable model");
        return;
      }
    }
    
    // Remove from local state
    setCustomizableModels(prev => prev.filter((_, i) => i !== index));
  };

  const getCategoryName = (categoryId: string) => {
    return categories.find(cat => cat._id === categoryId)?.name || "Unknown";
  };

  const getSubcategoryName = (subcategoryId: string) => {
    return subcategories.find(sub => sub._id === subcategoryId)?.name || "Unknown";
  };

  // Modal handlers
  const handleAddModel = () => {
    setModelForm({
      name: "",
      shape: "",
      model_url: "",
      image2d_url: "",
      height: "",
      depth: "",
      width: "",
      textureavailable: [],
      colorsavailable: [],
      customizable: false,
      price: ""
    });
    setModelFile(null);
    setImage2dFile(null);
    setIsAddModalOpen(true);
  };

  const handleEditModel = (model: Model) => {
    setEditingModel(model);
    setModelForm({
      name: model.name,
      shape: model.shape,
      model_url: model.model_url || "",
      image2d_url: model.image2d_url || "",
      height: model.height.toString(),
      depth: model.depth.toString(),
      width: model.width.toString(),
      textureavailable: model.textureavailable,
      colorsavailable: model.colorsavailable,
      customizable: model.customizable || false,
      price: model.price?.toString() || ""
    });
    
    // Set the selected category and subcategory
    setSelectedCategory(model.category);
    setSelectedSubcategory(model.subcategory);
    
    // If model is customizable and has modelCustomisable IDs, load those models
    if (model.customizable && model.modelCustomisable && model.modelCustomisable.length > 0) {
      // Find all models that match the IDs in modelCustomisable
      const customizableModelData = model.modelCustomisable
        .map(id => models.find(m => m._id === id))
        .filter(Boolean) as Model[];
      
      // Convert to CustomizableModel format
      const customizableModelsData: CustomizableModel[] = customizableModelData.map(model => ({
        _id: model._id,
        name: model.name,
        shape: model.shape,
        model_url: model.model_url,
        image2d_url: model.image2d_url,
        height: model.height,
        depth: model.depth,
        width: model.width,
        textureavailable: model.textureavailable,
        colorsavailable: model.colorsavailable,
        price: model.price,
        customizableProduct: true,
        customizable: false
      }));
      
      setCustomizableModels(customizableModelsData);
    } else {
      setCustomizableModels([]);
    }
    
    // Fetch subcategories for the selected category
    if (model.category) {
      fetchSubcategories(model.category);
    }
    
    setIsEditModalOpen(true);
  };

  const handleDeleteModel = async (modelId: string) => {
    if (window.confirm("Are you sure you want to delete this model?")) {
      try {
        setLoading(true);
        await api.delete(`${UPDATE_MODELS}/${modelId}`);
        await fetchModels(); // Refresh the list
      } catch (error) {
        console.error("Error deleting model:", error);
        setError("Failed to delete model");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    setModelForm({
      name: "",
      shape: "",
      model_url: "",
      image2d_url: "",
      height: "",
      depth: "",
      width: "",
      textureavailable: [],
      colorsavailable: [],
      customizable: false,
      price: ""
    });
    setModelFile(null);
    setImage2dFile(null);
    setError("");
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingModel(null);
    setModelForm({
      name: "",
      shape: "",
      model_url: "",
      image2d_url: "",
      height: "",
      depth: "",
      width: "",
      textureavailable: [],
      colorsavailable: [],
      customizable: false,
      price: ""
    });
    setCustomizableModels([]); // Clear customizable models
    setError("");
  };

  const handleSubmitAddModel = async () => {
    if (!modelForm.name.trim() || !modelForm.shape.trim() || !modelForm.height || !modelForm.depth || !modelForm.width) {
      setError("Name, shape, height, depth, and width are required");
      return;
    }

    if (!selectedCategory || !selectedSubcategory) {
      setError("Please select a category and subcategory from the filter section");
      return;
    }

    try {
      setLoading(true);
      setError("");
      
      const formData = new FormData();
      
      const modelData = {
        name: modelForm.name.trim(),
        shape: modelForm.shape.trim(),
        height: parseFloat(modelForm.height),
        depth: parseFloat(modelForm.depth),
        width: parseFloat(modelForm.width),
        textureavailable: modelForm.textureavailable,
        colorsavailable: modelForm.colorsavailable,
        category: selectedCategory,
        subcategory: selectedSubcategory,
        customizable: modelForm.customizable,
        price: parseFloat(modelForm.price) || 0,
        modelCustomisable: customizableModels.map(model => model._id).filter(Boolean), // Array of customizable model IDs
        customizableProduct: false
      };
      
      formData.append('data', JSON.stringify(modelData));
      
      if (modelFile) {
        formData.append('modelFile', modelFile);
      }
      if (image2dFile) {
        formData.append('image2d', image2dFile);
      }
      
      const response = await api.post(ADD_MODELS, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data) {
        await fetchModels();
        handleCloseAddModal();
      }
    } catch (error: any) {
      console.error("Error adding model:", error);
      setError(error.response?.data?.message || "Failed to add model");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitEditModel = async () => {
    if (!modelForm.name.trim() || !modelForm.shape.trim() || !modelForm.height || !modelForm.depth || !modelForm.width) {
      setError("Name, shape, height, depth, and width are required");
      return;
    }

    if (!selectedCategory || !selectedSubcategory) {
      setError("Please select a category and subcategory from the filter section");
      return;
    }

    try {
      setLoading(true);
      setError("");
      
      const response = await api.put(`${UPDATE_MODELS}/${editingModel?._id}`, {
        name: modelForm.name.trim(),
        shape: modelForm.shape.trim(),
        model_url: modelForm.model_url.trim(),
        image2d_url: modelForm.image2d_url.trim(),
        height: parseFloat(modelForm.height),
        depth: parseFloat(modelForm.depth),
        width: parseFloat(modelForm.width),
        textureavailable: modelForm.textureavailable,
        colorsavailable: modelForm.colorsavailable,
        category: selectedCategory,
        subcategory: selectedSubcategory,
        customizable: modelForm.customizable,
        price: parseFloat(modelForm.price) || 0,
        modelCustomisable: customizableModels.map(model => model._id).filter(Boolean), // Array of customizable model IDs
        customizableProduct: false
      });

      if (response.data) {
        await fetchModels();
        handleCloseEditModal();
      }
    } catch (error: any) {
      console.error("Error updating model:", error);
      setError(error.response?.data?.message || "Failed to update model");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitCustomizableModel = async () => {
    if (!customizableForm.name.trim() || !customizableForm.shape.trim() || !customizableForm.height || !customizableForm.depth || !customizableForm.width) {
      setError("Name, shape, height, depth, and width are required");
      return;
    }

    try {
      setLoading(true);
      setError("");
      
      // Create FormData for file upload
      const formData = new FormData();
      
      // Add the model data as JSON string
      const modelData = {
        name: customizableForm.name.trim(),
        shape: customizableForm.shape.trim(),
        height: parseFloat(customizableForm.height),
        depth: parseFloat(customizableForm.depth),
        width: parseFloat(customizableForm.width),
        textureavailable: customizableForm.textureavailable,
        colorsavailable: customizableForm.colorsavailable,
        category: selectedCategory,
        subcategory: selectedSubcategory,
        customizableProduct: true, // Set to true for customizable models
        price: parseFloat(customizableForm.price) || 0,
        customizable: false
      };
      
      formData.append('data', JSON.stringify(modelData));
      
      // Add files if they exist
      if (customizableModelFile) {
        formData.append('modelFile', customizableModelFile);
      }
      if (customizableImage2dFile) {
        formData.append('image2d', customizableImage2dFile);
      }
      
      const response = await api.post(ADD_MODELS, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data) {
        // Add the new customizable model to the list with its ID
        const newCustomizableModel: CustomizableModel = {
          _id: response.data.data?._id || response.data._id,
          name: customizableForm.name.trim(),
          shape: customizableForm.shape.trim(),
          height: parseFloat(customizableForm.height),
          depth: parseFloat(customizableForm.depth),
          width: parseFloat(customizableForm.width),
          textureavailable: customizableForm.textureavailable,
          colorsavailable: customizableForm.colorsavailable,
          price: parseFloat(customizableForm.price) || 0,
          customizableProduct: true,
          customizable: false
        };
        
        setCustomizableModels(prev => [...prev, newCustomizableModel]);
        handleCloseCustomizableModal();
      }
    } catch (error: any) {
      console.error("Error adding customizable model:", error);
      setError(error.response?.data?.message || "Failed to add customizable model");
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="p-6 w-full max-w-full">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          All Models
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Manage all models
        </p>
      </div>

      {/* Category and Subcategory Selection */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Filter Models
          </h2>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Select Category
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => handleCategoryChange(e.target.value)}
              className="w-full h-11 rounded-lg border border-gray-300 dark:border-gray-700 px-4 py-2.5 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select a category</option>
              {categories.map(category => (
                <option key={category._id} value={category._id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Select Subcategory
            </label>
            <select
              value={selectedSubcategory}
              onChange={(e) => handleSubcategoryChange(e.target.value)}
              className="w-full h-11 rounded-lg border border-gray-300 dark:border-gray-700 px-4 py-2.5 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={!selectedCategory}
            >
              <option value="">Select a subcategory</option>
              {subcategories.map(subcategory => (
                <option key={subcategory._id} value={subcategory._id}>
                  {subcategory.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {selectedCategory && selectedSubcategory && (
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-sm text-blue-600 dark:text-blue-400">
              Showing models for: <span className="font-medium">{getCategoryName(selectedCategory)}</span> → <span className="font-medium">{getSubcategoryName(selectedSubcategory)}</span>
            </p>
          </div>
        )}
      </div>

      {/* Add Model Button */}
      <div className="mb-6">
        <Button
          onClick={handleAddModel}
          variant="primary"
          size="md"
          startIcon={
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 5V19M5 12H19"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
        >
          Add New Model
        </Button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p className="text-sm text-blue-600 dark:text-blue-400">Loading models...</p>
        </div>
      )}

      {/* Models Grid */}
      {models.length === 0 && !loading ? (
        <div className="text-center py-12">
          <div className="text-gray-400 dark:text-gray-500 mb-4">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="mx-auto">
              <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <p className="text-gray-500 dark:text-gray-400 text-lg">No models found</p>
          <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">
            {selectedCategory && selectedSubcategory 
              ? `No models found for ${getCategoryName(selectedCategory)} → ${getSubcategoryName(selectedSubcategory)}`
              : "Please select a category and subcategory to view models"
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
          {models.map((model) => (
            <div key={model._id} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow duration-200">
              {/* Model Image */}
              <div className="aspect-square bg-gray-200 dark:bg-gray-600 overflow-hidden">
                {model.image2d_url ? (
                  <img
                    src={model.image2d_url}
                    alt={model.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                )}
              </div>

              {/* Model Details */}
              <div className="p-4">
                <div className="space-y-3">
                  {/* Model Name */}
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Model Name
                    </label>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mt-1 truncate">
                      {model.name}
                    </h3>
                  </div>

                  {/* Shape */}
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Shape
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white mt-1">
                      {model.shape}
                    </p>
                  </div>

                  {/* Dimensions */}
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Dimensions
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white mt-1">
                      {model.width} × {model.depth} × {model.height}
                    </p>
                  </div>

                  {/* Category & Subcategory */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        Category
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white mt-1 truncate">
                        {getCategoryName(model.category)}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        Subcategory
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white mt-1 truncate">
                        {getSubcategoryName(model.subcategory)}
                      </p>
                    </div>
                  </div>

                  {/* Price */}
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Price
                    </label>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                      ${model.price || 0}
                    </p>
                  </div>

                  {/* Customizable Status */}
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Customizable
                    </label>
                    <div className="mt-1">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        model.customizable 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' 
                          : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                      }`}>
                        {model.customizable ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-4 flex gap-2">
                  <Button
                    onClick={() => handleEditModel(model)}
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    startIcon={
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="m18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    }
                  >
                    Edit
                  </Button>
                  <Button
                    onClick={() => handleDeleteModel(model._id)}
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:border-red-300"
                    startIcon={
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    }
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Model Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-auto mt-0 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Add New Model
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Model Name *
                </label>
                <Input
                  type="text"
                  placeholder="Enter model name"
                  value={modelForm.name}
                  onChange={(e) => setModelForm({...modelForm, name: e.target.value})}
                  error={!!error}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Shape *
                </label>
                <Input
                  type="text"
                  placeholder="Enter shape"
                  value={modelForm.shape}
                  onChange={(e) => setModelForm({...modelForm, shape: e.target.value})}
                  error={!!error}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Height *
                </label>
                <Input
                  type="number"
                  placeholder="Enter height"
                  value={modelForm.height}
                  onChange={(e) => setModelForm({...modelForm, height: e.target.value})}
                  error={!!error}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Depth *
                </label>
                <Input
                  type="number"
                  placeholder="Enter depth"
                  value={modelForm.depth}
                  onChange={(e) => setModelForm({...modelForm, depth: e.target.value})}
                  error={!!error}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Width *
                </label>
                <Input
                  type="number"
                  placeholder="Enter width"
                  value={modelForm.width}
                  onChange={(e) => setModelForm({...modelForm, width: e.target.value})}
                  error={!!error}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Price
                </label>
                <Input
                  type="number"
                  placeholder="Enter price"
                  value={modelForm.price}
                  onChange={(e) => setModelForm({...modelForm, price: e.target.value})}
                  step={0.01}
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Model File
                </label>
                <input
                  type="file"
                  accept=".obj,.fbx,.gltf,.glb,.dae,.3ds,.blend"
                  onChange={(e) => setModelFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 dark:file:bg-gray-700 dark:file:text-gray-300 dark:hover:file:bg-gray-600"
                />
                {modelFile && (
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Selected: {modelFile.name}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Image 2D File
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setImage2dFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 dark:file:bg-gray-700 dark:file:text-gray-300 dark:hover:file:bg-gray-600"
                />
                {image2dFile && (
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Selected: {image2dFile.name}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Available Colors
                </label>
                <Input
                  type="text"
                  placeholder="Comma separated colors"
                  value={modelForm.colorsavailable.join(", ")}
                  onChange={(e) => setModelForm({...modelForm, colorsavailable: e.target.value.split(",").map(c => c.trim()).filter(c => c)})}
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="customizable-add"
                  checked={modelForm.customizable}
                  onChange={(e) => setModelForm({...modelForm, customizable: e.target.checked})}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="customizable-add" className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Customizable
                </label>
              </div>
            </div>

            {/* Customizable Models Section */}
            {modelForm.customizable && (
              <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Customizable Models
                  </h4>
                  <Button
                    onClick={handleAddCustomizableModel}
                    variant="primary"
                    size="sm"
                    startIcon={
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 5V19M5 12H19"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    }
                  >
                    Add Model
                  </Button>
                </div>

                {customizableModels.length === 0 ? (
                  <div className="text-center py-6">
                    <p className="text-gray-500 dark:text-gray-400 mb-4">
                      No customizable models added yet. Click "Add Model" to add customizable variations.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {customizableModels.map((model, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                        <div className="flex items-center gap-4 flex-1">
                          {/* 2D Image Display */}
                          <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-200 dark:bg-gray-600 flex-shrink-0">
                            {model.image2d_url ? (
                              <img
                                src={model.image2d_url}
                                alt={model.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex-1">
                            <div className="flex items-center gap-4">
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white">
                                  {model.name}
                                </p>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  {model.shape} • {model.width} × {model.depth} × {model.height}
                                </p>
                              </div>
                              <div className="text-sm text-gray-600 dark:text-gray-400">
                                ${model.price}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <Button
                          onClick={() => handleRemoveCustomizableModel(index)}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:border-red-300"
                          startIcon={
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M18 6L6 18M6 6l12 12"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          }
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                size="md"
                onClick={handleCloseAddModal}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleSubmitAddModel}
                disabled={loading || !modelForm.name.trim() || !modelForm.shape.trim() || !modelForm.height || !modelForm.depth || !modelForm.width || !selectedCategory || !selectedSubcategory}
                loading={loading}
              >
                Add Model
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Model Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-auto mt-0 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Edit Model
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Model Name *
                </label>
                <Input
                  type="text"
                  placeholder="Enter model name"
                  value={modelForm.name}
                  onChange={(e) => setModelForm({...modelForm, name: e.target.value})}
                  error={!!error}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Shape *
                </label>
                <Input
                  type="text"
                  placeholder="Enter shape"
                  value={modelForm.shape}
                  onChange={(e) => setModelForm({...modelForm, shape: e.target.value})}
                  error={!!error}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Height *
                </label>
                <Input
                  type="number"
                  placeholder="Enter height"
                  value={modelForm.height}
                  onChange={(e) => setModelForm({...modelForm, height: e.target.value})}
                  error={!!error}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Depth *
                </label>
                <Input
                  type="number"
                  placeholder="Enter depth"
                  value={modelForm.depth}
                  onChange={(e) => setModelForm({...modelForm, depth: e.target.value})}
                  error={!!error}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Width *
                </label>
                <Input
                  type="number"
                  placeholder="Enter width"
                  value={modelForm.width}
                  onChange={(e) => setModelForm({...modelForm, width: e.target.value})}
                  error={!!error}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Price
                </label>
                <Input
                  type="number"
                  placeholder="Enter price"
                  value={modelForm.price}
                  onChange={(e) => setModelForm({...modelForm, price: e.target.value})}
                  step={0.01}
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Model URL
                </label>
                <Input
                  type="url"
                  placeholder="Enter model URL"
                  value={modelForm.model_url}
                  onChange={(e) => setModelForm({...modelForm, model_url: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Image 2D URL
                </label>
                <Input
                  type="url"
                  placeholder="Enter image URL"
                  value={modelForm.image2d_url}
                  onChange={(e) => setModelForm({...modelForm, image2d_url: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Available Colors
                </label>
                <Input
                  type="text"
                  placeholder="Comma separated colors"
                  value={modelForm.colorsavailable.join(", ")}
                  onChange={(e) => setModelForm({...modelForm, colorsavailable: e.target.value.split(",").map(c => c.trim()).filter(c => c)})}
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="customizable-edit"
                  checked={modelForm.customizable}
                  onChange={(e) => setModelForm({...modelForm, customizable: e.target.checked})}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="customizable-edit" className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Customizable
                </label>
              </div>
            </div>

            {/* Customizable Models Section */}
            {modelForm.customizable && (
              <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Customizable Models
                  </h4>
                  <Button
                    onClick={handleAddCustomizableModel}
                    variant="primary"
                    size="sm"
                    startIcon={
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 5V19M5 12H19"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    }
                  >
                    Add Model
                  </Button>
                </div>

                {customizableModels.length === 0 ? (
                  <div className="text-center py-6">
                    <p className="text-gray-500 dark:text-gray-400 mb-4">
                      No customizable models added yet. Click "Add Model" to add customizable variations.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {customizableModels.map((model, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                        <div className="flex items-center gap-4 flex-1">
                          {/* 2D Image Display */}
                          <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-200 dark:bg-gray-600 flex-shrink-0">
                            {model.image2d_url ? (
                              <img
                                src={model.image2d_url}
                                alt={model.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex-1">
                            <div className="flex items-center gap-4">
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white">
                                  {model.name}
                                </p>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  {model.shape} • {model.width} × {model.depth} × {model.height}
                                </p>
                              </div>
                              <div className="text-sm text-gray-600 dark:text-gray-400">
                                ${model.price}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <Button
                          onClick={() => handleRemoveCustomizableModel(index)}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:border-red-300"
                          startIcon={
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M18 6L6 18M6 6l12 12"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          }
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                size="md"
                onClick={handleCloseEditModal}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleSubmitEditModel}
                disabled={loading || !modelForm.name.trim() || !modelForm.shape.trim() || !modelForm.height || !modelForm.depth || !modelForm.width || !selectedCategory || !selectedSubcategory}
                loading={loading}
              >
                Update Model
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Customizable Model Modal */}
      {isCustomizableModalOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-auto mt-0 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Add Customizable Model
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Model Name *
                </label>
                <Input
                  type="text"
                  placeholder="Enter model name"
                  value={customizableForm.name}
                  onChange={(e) => setCustomizableForm({...customizableForm, name: e.target.value})}
                  error={!!error}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Shape *
                </label>
                <Input
                  type="text"
                  placeholder="Enter shape"
                  value={customizableForm.shape}
                  onChange={(e) => setCustomizableForm({...customizableForm, shape: e.target.value})}
                  error={!!error}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Height *
                </label>
                <Input
                  type="number"
                  placeholder="Enter height"
                  value={customizableForm.height}
                  onChange={(e) => setCustomizableForm({...customizableForm, height: e.target.value})}
                  error={!!error}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Depth *
                </label>
                <Input
                  type="number"
                  placeholder="Enter depth"
                  value={customizableForm.depth}
                  onChange={(e) => setCustomizableForm({...customizableForm, depth: e.target.value})}
                  error={!!error}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Width *
                </label>
                <Input
                  type="number"
                  placeholder="Enter width"
                  value={customizableForm.width}
                  onChange={(e) => setCustomizableForm({...customizableForm, width: e.target.value})}
                  error={!!error}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Model File
                </label>
                <input
                  type="file"
                  accept=".obj,.fbx,.gltf,.glb,.dae,.3ds,.blend"
                  onChange={(e) => setCustomizableModelFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 dark:file:bg-gray-700 dark:file:text-gray-300 dark:hover:file:bg-gray-600"
                />
                {customizableModelFile && (
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Selected: {customizableModelFile.name}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Image 2D File
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setCustomizableImage2dFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 dark:file:bg-gray-700 dark:file:text-gray-300 dark:hover:file:bg-gray-600"
                />
                {customizableImage2dFile && (
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Selected: {customizableImage2dFile.name}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Available Colors
                </label>
                <Input
                  type="text"
                  placeholder="Comma separated colors"
                  value={customizableForm.colorsavailable.join(", ")}
                  onChange={(e) => setCustomizableForm({...customizableForm, colorsavailable: e.target.value.split(",").map(c => c.trim()).filter(c => c)})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Price
                </label>
                <Input
                  type="number"
                  placeholder="Enter price"
                  value={customizableForm.price}
                  onChange={(e) => setCustomizableForm({...customizableForm, price: e.target.value})}
                  step={0.01}
                  min="0"
                />
              </div>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                size="md"
                onClick={handleCloseCustomizableModal}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleSubmitCustomizableModel}
                disabled={loading || !customizableForm.name.trim() || !customizableForm.shape.trim() || !customizableForm.height || !customizableForm.depth || !customizableForm.width}
                loading={loading}
              >
                Add Customizable Model
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllModels;
